import React from 'react'

import { Alert, Box, Text } from '@factorialco/deprecated-design-system'
import { supportedTimezones } from 'config/Vars'
import { AtsCompletedCalendarSyncFragment } from 'generated/resources/graphql'

import DurationInput from 'modules/core/components/DurationInput'
import SelectField from 'modules/core/components/SelectField'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { timezoneToString } from 'modules/core/lib/timezones'

import { AvailabilityViewer } from 'modules/cronofy/components/AvailabilityViewer'
import { useGenerateElementToken } from 'modules/cronofy/hooks/useGenerateElementToken'

import { CreateInterviewData } from './CreateInterviewData'

interface Props {
  data: CreateInterviewData
  onDataChanged: (data: CreateInterviewData) => void
  calendarSyncsByAccessId: { [id: string]: AtsCompletedCalendarSyncFragment }
}

export const isDateTimeCompleted = (data: CreateInterviewData) =>
  !!data.date && !!data.timezone && !!data.minutes

const DEFAULT_DURATION = 60

export const DateTimeStep = ({ data, onDataChanged, calendarSyncsByAccessId }: Props) => {
  const [elementToken, setElementToken] = React.useState<string | undefined>(undefined)
  const [customDuration, setCustomDuration] = React.useState(false)
  const [availabilityOptions, setAvailabilityOptions] =
    React.useState<React.ComponentProps<typeof AvailabilityViewer>['options']>()
  const { generate } = useGenerateElementToken()
  const attendeesSubs = Array.from(
    new Set(
      data.attendeeIds
        .map((attendeeId) => calendarSyncsByAccessId[attendeeId]?.sub)
        .filter((s): s is string => Boolean(s))
    )
  )
  const organizerAccessId = data.organizerAccessId ?? '0'
  const organizerCalendarSync = calendarSyncsByAccessId[organizerAccessId]
  const hasAttendeesCalendarAccess = attendeesSubs.length > 0

  const today = new Date()
  const endDate = new Date()
  endDate.setDate(endDate.getDate() + 30)

  const generateElementToken = async () => {
    if (!organizerCalendarSync) return

    const result = await generate({
      id: organizerCalendarSync.id,
      attendeesAccessesIds: hasAttendeesCalendarAccess ? data.attendeeIds : [organizerAccessId],
    })

    const handled = handleResult(result, () => undefined)
    const token = handled.success && handled.data.calendarSync?.elementToken
    if (token) {
      setElementToken(token)
    }
  }

  useUnsafeEffect(() => {
    if (data.attendeeIds.length > 0) {
      generateElementToken()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.organizerAccessId, data.attendeeIds.toString()])

  const durationOptions = [
    {
      label: i18n.t('ats.schedule_interview.steps.date_time.durations.x_minutes', { minutes: 30 }),
      value: 30,
    },
    {
      label: i18n.t('ats.schedule_interview.steps.date_time.durations.x_minutes', { minutes: 45 }),
      value: 45,
    },
    { label: i18n.t('ats.schedule_interview.steps.date_time.durations.1_hour'), value: 60 },
    { label: i18n.t('ats.schedule_interview.steps.date_time.durations.custom'), value: '' },
  ]

  const timezoneOptions = supportedTimezones.map((timezone) => ({
    label: timezoneToString(timezone),
    value: timezone,
  }))

  useUnsafeEffect(() => {
    if (!data.minutes || !data.timezone) {
      return
    }

    setAvailabilityOptions({
      availability_query: {
        participants: [
          {
            required: 'all',
            members: hasAttendeesCalendarAccess
              ? attendeesSubs.map((sub) => ({ sub }))
              : organizerCalendarSync?.sub
                ? [{ sub: organizerCalendarSync.sub }]
                : [],
          },
        ],
        query_periods: [{ start: today.toISOString(), end: endDate.toISOString() }],
        required_duration: { minutes: data.minutes || 60 },
        include_member_statuses: true,
      },
      config: {
        start_time: '09:00',
        end_time: '18:00',
        interval: 15,
        slot_selection: 'unrestricted',
        mode: 'no_confirm',
        allow_expansion: true,
      },
      tzid: data.timezone,
      locale: data.locale,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.minutes, data.timezone, data.locale, attendeesSubs.join(',')])

  const onDurationChange = (value: string) => {
    if (value === '') {
      setCustomDuration(true)
    } else {
      onDataChanged({
        ...data,
        minutes: parseInt(value),
      })
    }
  }

  const onDateTimeChange = (date: string) => {
    onDataChanged({
      ...data,
      date,
    })
  }

  return (
    <Box gap="s24">
      <Box flexDirection="row" gap="s16">
        {!customDuration && (
          <SelectField
            label={i18n.t('ats.schedule_interview.steps.date_time.duration')}
            onChange={onDurationChange}
            value={(data.minutes || DEFAULT_DURATION).toString()}
            options={durationOptions}
          />
        )}
        {customDuration && (
          <DurationInput
            label={i18n.t('ats.schedule_interview.steps.date_time.duration')}
            minutes={data.minutes || DEFAULT_DURATION}
            onChange={(minutes) =>
              onDataChanged({
                ...data,
                minutes,
              })
            }
          />
        )}

        <SelectField
          label={i18n.t('ats.schedule_interview.steps.date_time.timezone')}
          onChange={(value) => {
            onDataChanged({
              ...data,
              timezone: value,
            })
          }}
          value={data.timezone}
          options={timezoneOptions}
        />
      </Box>

      {!hasAttendeesCalendarAccess && (
        <Alert>
          <Text.Normal size="200">
            {i18n.t('ats.schedule_interview.steps.date_time.no_calendar_warn')}
          </Text.Normal>
        </Alert>
      )}

      {!!elementToken && !!availabilityOptions && (
        <AvailabilityViewer
          elementToken={elementToken}
          options={availabilityOptions}
          onChange={onDateTimeChange}
        />
      )}
    </Box>
  )
}
